'use client';

import type { ReactNode } from 'react';
import { automationId } from '@terra/shared-utils';
import { HStack } from '@terra/ui-styled-system';
import { Button } from '../../components/button';
import { BodyDefault } from '../../components/typography';

export type ContextNavigationItem<TValue extends string> = {
  value: TValue;
  label: string;
  count?: number;
  disabled?: boolean;
  dataTestid?: string;
};

export type ContextNavigationProps<TValue extends string> = {
  items: ReadonlyArray<ContextNavigationItem<TValue>>;
  value: TValue | null;
  onValueChange: (value: TValue) => void;
  ariaLabel: string;
  actions?: ReactNode;
};

export function ContextNavigation<TValue extends string>({
  items,
  value,
  onValueChange,
  ariaLabel,
  actions,
}: ContextNavigationProps<TValue>) {
  const switchableItems = items.length > 1 ? items : [];

  return (
    <HStack gap="2" alignItems="center" w="full" minH="7">
      <HStack
        role="group"
        aria-label={ariaLabel}
        gap="2"
        alignItems="center"
        flex="1"
        minW="0"
        overflowX="auto"
      >
        {switchableItems.map((item) => {
          const isSelected = item.value === value;

          return (
            <Button
              key={item.value}
              type="button"
              size="xs"
              variant="ghost"
              h="7"
              gap="2"
              px="2.5"
              borderRadius="full"
              flexShrink={0}
              bg={isSelected ? 'bg.neutral.muted' : 'transparent'}
              disabled={item.disabled}
              aria-pressed={isSelected}
              onClick={() => onValueChange(item.value)}
              {...automationId(item.dataTestid ?? '')}
            >
              <BodyDefault
                as="span"
                fontWeight="medium"
                whiteSpace="nowrap"
                color={isSelected ? 'fg.neutral.default' : 'fg.neutral.muted'}
              >
                {item.label}
              </BodyDefault>
              {item.count === undefined ? null : (
                <BodyDefault
                  as="span"
                  whiteSpace="nowrap"
                  color={isSelected ? 'fg.neutral.default' : 'fg.neutral.muted'}
                >
                  {item.count.toLocaleString()}
                </BodyDefault>
              )}
            </Button>
          );
        })}
      </HStack>
      {actions ? (
        <HStack gap="2" alignItems="center" flexShrink={0}>
          {actions}
        </HStack>
      ) : null}
    </HStack>
  );
}
